import React from "react";
import { useEffect, useState } from "react";

import { ButtonStandard } from "@performyard/py-components";
import { Text as Typography } from "@performyard/py-components";

import { ContentModal } from "../../../../../../components/Modal/Templates/ContentModal";
import Spacer from "../../../../../../components/Spacer";

import { useSetters as useSurveyBuilderSetters } from "../../pages/SurveyBuilder/store/store";
import { useSetters as useSurveyParticipantsSetters } from "../../pages/SurveyParticipants/store/store";

import { router } from "../../../../../../app/router";

import { SURVEY_DETAILS_MODALS } from "../../store/model";
import { useModal, useSetters } from "../../store/store";

import { useDeleteSurvey } from "../../../../../../api/hooks/defs/surveys";
import { useUpdateSurvey } from "../../../../../../api/hooks/defs/surveys";
import { useSurvey } from "../../../SurveyDetails/hooks/useSurvey";

export const CloseOrDeleteSurveyModal = ({ surveyId, resetForm }: { surveyId: string; resetForm: () => void }) => {
	const modal = useModal();
	const setters = useSetters();
	const surveyBuilderSetters = useSurveyBuilderSetters();
	const surveyParticipantsSetters = useSurveyParticipantsSetters();
	const { survey } = useSurvey({ surveyId });

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [title, setTitle] = useState<string>("");
	const [buttonText, setButtonText] = useState<string>("");
	const [buttonColor, setButtonColor] = useState<"primary" | "green" | "red" | "neutral" | undefined>(undefined);
	const [keyPrefix, setKeyPrefix] = useState<string>("");
	const [bodyText, setBodyText] = useState<string>("");

	const onSuccessClose = async () => {
		resetForm();
		setters.setModal(null);
	};
	const onSuccessDelete = async () => {
		resetForm();
		setters.setModal(null);
		router.navigate("/surveys");
	};
	const onError = async () => {
		setters.setModal(null);
	};

	const { mutate: deleteSurvey } = useDeleteSurvey({ onSuccess: onSuccessDelete, onError });
	const { mutate: closeSurvey } = useUpdateSurvey(surveyId, { onSuccess: onSuccessClose, onError });

	const isCloseSurvey = modal === SURVEY_DETAILS_MODALS.CLOSE_SURVEY;
	const isDeleteSurvey = modal === SURVEY_DETAILS_MODALS.DELETE_SURVEY;

	useEffect(() => {
		setIsOpen(isCloseSurvey || isDeleteSurvey);
	}, [isCloseSurvey, isDeleteSurvey]);

	useEffect(() => {
		switch (modal) {
			case SURVEY_DETAILS_MODALS.CLOSE_SURVEY:
				setTitle("Close Survey?");
				setButtonText("Yes, Close Survey");
				setButtonColor("green");
				setKeyPrefix("close");
				setBodyText("Your survey will no longer receive responses.");
				break;
			case SURVEY_DETAILS_MODALS.DELETE_SURVEY:
				setTitle("Delete Survey?");
				setButtonText("Yes, Delete Survey");
				setButtonColor("red");
				setKeyPrefix("delete");
				setBodyText("All results and data from this survey will be lost.");
				break;
			default:
				break;
		}
	}, [modal]);

	const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		if (!survey) {
			return;
		}
		if (isCloseSurvey) {
			closeSurvey({ status: "closed" });
		} else if (isDeleteSurvey) {
			surveyBuilderSetters.setHasChanges(false);
			surveyParticipantsSetters.setIsSafeToLeave(true);
			deleteSurvey(surveyId);
		}
	};

	const handleClose = () => {
		setIsOpen(false);
		setters.setModal(null);
	};

	return (
		<ContentModal size="small" isOpen={isOpen} handleClose={handleClose}>
			<div className="flex w-full flex-col items-center">
				<Typography size="sub-heading" weight="semi-bold">
					{title}
				</Typography>
				<div className="min-w-0 max-w-[360px] pb-4">
					<div className="line-clamp-1">
						<Typography weight="regular" size="paragraph-1">
							{survey?.name}
						</Typography>
					</div>
				</div>
				<Typography size="paragraph-1" weight="regular">
					<div className="text-center">{bodyText}</div>
				</Typography>
				<Spacer height={3} />
				<div className="flex justify-center gap-2 pb-4">
					<div key={`${keyPrefix}-survey-cancel`}>
						<ButtonStandard size="large" type="button" onClick={handleClose}>
							Cancel
						</ButtonStandard>
					</div>
					<div key={`${keyPrefix}-survey-submit`}>
						<ButtonStandard size="large" type="submit" color={buttonColor} onClick={handleSubmit}>
							{buttonText}
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ContentModal>
	);
};
